import React from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { variants, VIEWPORT } from '../../lib/motion-presets';

/**
 * <Stagger> — container that staggers its <Stagger.Item> children into view.
 *
 * <Stagger as="ul" stagger={0.08}>
 *   <Stagger.Item as="li">…</Stagger.Item>
 *   <Stagger.Item as="li">…</Stagger.Item>
 * </Stagger>
 *
 * Passes through className/style. Honors reduced motion (snaps visible).
 */
const Stagger = ({
  as = 'div',
  stagger = 0.08,
  delayChildren = 0.05,
  once = true,
  amount = 0.2,
  className = '',
  style,
  children,
  ...rest
}) => {
  const reduce = useReducedMotion();
  const MotionTag = motion[as] || motion.div;
  const container = reduce
    ? { hidden: {}, visible: { transition: { staggerChildren: 0 } } }
    : variants.stagger(stagger, delayChildren);

  return (
    <MotionTag
      className={className}
      style={style}
      initial="hidden"
      whileInView="visible"
      viewport={{ once, amount, margin: VIEWPORT.margin }}
      variants={container}
      {...rest}
    >
      {children}
    </MotionTag>
  );
};

const Item = ({
  as = 'div',
  variant = 'staggerItem',
  className = '',
  style,
  children,
  ...rest
}) => {
  const reduce = useReducedMotion();
  const MotionTag = motion[as] || motion.div;
  const v = variant === 'staggerItem' ? variants.staggerItem : variants[variant];
  const resolved = reduce
    ? { hidden: { opacity: 1 }, visible: { opacity: 1, transition: { duration: 0 } } }
    : v;

  return (
    <MotionTag
      className={className}
      style={style}
      variants={resolved}
      {...rest}
    >
      {children}
    </MotionTag>
  );
};

Stagger.Item = Item;

export default Stagger;
